/**
 * Site map state
 *
 * Pure reducers for the site list and the polled health. Failures keep the last good data
 * and record the error, so the page can keep showing the map with a warning. Times are
 * passed in actions so the reducers stay pure.
 */

import type { SiteMapResult } from '../api/siteMapApiClient';
import type { SiteMapHealth, SiteMapSites } from '../models/siteMapModels';

// ---------------------------------------------------------------------------
// Site list
// ---------------------------------------------------------------------------

export interface SitesState {
  readonly data: SiteMapSites | null;
  /** When data last loaded successfully (epoch ms) */
  readonly loadedAt: number | null;
  /** When the list was last requested, successfully or not (epoch ms) */
  readonly lastRequestedAt: number | null;
  /** True only while there is no data to show yet */
  readonly isLoading: boolean;
  readonly isForbidden: boolean;
  /** Most recent failure. Data, if any, is from the last successful request. */
  readonly error: string | null;
}

export type SitesAction =
  | { readonly type: 'requested'; readonly at: number }
  | { readonly type: 'loaded'; readonly data: SiteMapSites; readonly at: number }
  | { readonly type: 'forbidden' }
  | { readonly type: 'failed'; readonly message: string };

export const initialSitesState: SitesState = {
  data: null,
  loadedAt: null,
  lastRequestedAt: null,
  isLoading: true,
  isForbidden: false,
  error: null,
};

export function sitesReducer(state: SitesState, action: SitesAction): SitesState {
  switch (action.type) {
    case 'requested':
      return state.data === null
        ? { ...state, lastRequestedAt: action.at, isLoading: true, error: null }
        : { ...state, lastRequestedAt: action.at };
    case 'loaded':
      return {
        ...state,
        data: action.data,
        loadedAt: action.at,
        isLoading: false,
        isForbidden: false,
        error: null,
      };
    case 'forbidden':
      return { ...state, data: null, loadedAt: null, isLoading: false, isForbidden: true, error: null };
    case 'failed':
      return { ...state, isLoading: false, error: action.message };
    default: {
      const unhandled: never = action;
      return unhandled;
    }
  }
}

export function sitesResultToAction(result: SiteMapResult<SiteMapSites>, at: number): SitesAction {
  switch (result.status) {
    case 'ok':
      return { type: 'loaded', data: result.data, at };
    case 'forbidden':
      return { type: 'forbidden' };
    case 'error':
      return { type: 'failed', message: result.message };
    default: {
      const unhandled: never = result;
      return unhandled;
    }
  }
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export interface HealthState {
  readonly data: SiteMapHealth | null;
  /** When health last loaded successfully (epoch ms) */
  readonly lastSuccessAt: number | null;
  readonly isLoading: boolean;
  /** Most recent failure. Data, if any, is from the last successful poll. */
  readonly error: string | null;
}

export type HealthAction =
  | { readonly type: 'loaded'; readonly data: SiteMapHealth; readonly at: number }
  | { readonly type: 'failed'; readonly message: string };

export const initialHealthState: HealthState = {
  data: null,
  lastSuccessAt: null,
  isLoading: true,
  error: null,
};

export function healthReducer(state: HealthState, action: HealthAction): HealthState {
  switch (action.type) {
    case 'loaded':
      return { data: action.data, lastSuccessAt: action.at, isLoading: false, error: null };
    case 'failed':
      return { ...state, isLoading: false, error: action.message };
    default: {
      const unhandled: never = action;
      return unhandled;
    }
  }
}

/**
 * Forbidden is a failure here: access to the whole page is decided by the site list,
 * so health only needs to say it could not be read.
 */
export function healthResultToAction(
  result: SiteMapResult<SiteMapHealth>,
  at: number
): HealthAction {
  switch (result.status) {
    case 'ok':
      return { type: 'loaded', data: result.data, at };
    case 'forbidden':
      return { type: 'failed', message: 'Not allowed to read site health' };
    case 'error':
      return { type: 'failed', message: result.message };
    default: {
      const unhandled: never = result;
      return unhandled;
    }
  }
}
